import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CloseButton } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, RadioCircle, RadioInput } from '@/components/Forms';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';
import Text from '@/components/Text';

import { CHANNEL_IMAGE_OPTIONS, CHANNEL_NAME_COLORS } from '.';
import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadSingleImage from '../UploadSingleImage';
import { ImageData, LabelText, UploadedImages } from '../VariationOne';
import { DIVIDERS } from '../VariationTwo';
import {
  WATERMARK_POSITIONS,
  WATERMARK_TAGS,
  getWatermarkPosition,
  getWatermarkTag,
} from '../shared';
import {
  ActionBox,
  ActionTitle,
  Dialog,
  ModalContent,
  ModalHeader,
  StyledRadiosLabel,
} from '../shared/styledComponents';

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

interface Props {
  image: ImageData;
  imageIndex: number;
  onClose: () => void;
  setImages: React.Dispatch<React.SetStateAction<ImageData[]>>;
  multipleVariations?: boolean;
}

const SettingsModal: FC<Props> = ({
  onClose,
  image,
  setImages,
  imageIndex,
  multipleVariations,
}) => {
  const [divider, setDivider] = useState(image.divider);
  const [watermarkTag, setWatermarkTag] = useState(getWatermarkTag(image));
  const [watermarkPosition, setWatermarkPosition] = useState(
    getWatermarkPosition(image),
  );
  const [brakes, setBrakes] = useState(image.brakes);
  const [fontSize, setFontSize] = useState(image.fontSize);
  const [channelImageOption, setChannelImageOption] = useState(
    image.channelImageOption ?? 'custom',
  );
  const [authorImages, setAuthorImages] = useState<ImageData[]>(
    image.authorImageOriginal ?? [],
  );
  const [authorName, setAuthorName] = useState(image.authorName);
  const [nameColor, setNameColor] = useState(image.nameColor ?? 'black');

  const onUpdateSettings = () => {
    setImages((prev) => {
      const newArray = [...prev];
      newArray[imageIndex] = {
        ...image,
        brakes,
        divider,
        fontSize,
        watermarkTag,
        watermarkPosition,
        authorName,
        nameColor,
        channelImageOption,
        authorImageOriginal: authorImages,
        authorImage: !!authorImages.length
          ? authorImages[0]?.croppedImage ??
            URL.createObjectURL(authorImages[0].image)
          : undefined,
      };
      return newArray;
    });
    onClose();
  };

  const renderAuthorImages = useMemo(
    () =>
      authorImages.map((image, index) => (
        <SimpleUploadedImageRow
          key={index}
          imageIndex={index}
          image={image}
          setImages={setAuthorImages}
        />
      )),
    [authorImages, setAuthorImages],
  );

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <Text $type="button">Image Parameters</Text>
          <CloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <div>
            <ActionTitle $type="smallHeadline">Watermark</ActionTitle>
            <ActionBox $columns={2}>
              {WATERMARK_TAGS.map((option) => (
                <StyledRadiosLabel key={option.value}>
                  {option.label}
                  <RadioInput
                    type="radio"
                    name="watermark_tag"
                    checked={watermarkTag === option.value}
                    onChange={() => setWatermarkTag(option.value)}
                  />
                  <RadioCircle />
                </StyledRadiosLabel>
              ))}
            </ActionBox>
          </div>
          <div>
            <ActionTitle $type="smallHeadline">Watermark Position</ActionTitle>
            <ActionBox>
              {WATERMARK_POSITIONS.map((value) => (
                <StyledRadiosLabel key={value}>
                  {value}
                  <RadioInput
                    type="radio"
                    name="watermark_position"
                    checked={watermarkPosition === value}
                    onChange={() => setWatermarkPosition(value)}
                  />
                  <RadioCircle />
                </StyledRadiosLabel>
              ))}
            </ActionBox>
          </div>
          <div>
            <ActionTitle $type="smallHeadline">Cover Divider</ActionTitle>
            <ActionBox>
              {DIVIDERS.map((dividerOption) => (
                <StyledRadiosLabel key={dividerOption}>
                  {dividerOption}
                  <RadioInput
                    type="radio"
                    name="divider"
                    checked={divider === dividerOption}
                    onChange={() => setDivider(dividerOption)}
                  />
                  <RadioCircle />
                </StyledRadiosLabel>
              ))}
            </ActionBox>
          </div>
          {multipleVariations && (
            <>
              <div>
                <ActionTitle $type="smallHeadline">Text Parameters</ActionTitle>
                <ActionBox>
                  <Checkbox
                    checked={brakes}
                    onChange={() => setBrakes((prev) => !prev)}
                  >
                    Custom brakes
                  </Checkbox>
                </ActionBox>
              </div>
              <div>
                <ActionTitle $type="smallHeadline">Font Size</ActionTitle>
                <Input
                  value={fontSize}
                  onChange={(e) => setFontSize(parseInt(e.target.value))}
                  placeholder="Font Size (px)"
                  type="number"
                />
              </div>
              <div>
                <ActionTitle $type="smallHeadline">Channel Image</ActionTitle>
                <ActionBox>
                  {CHANNEL_IMAGE_OPTIONS.map((option) => (
                    <StyledRadiosLabel key={option}>
                      {option}
                      <RadioInput
                        type="radio"
                        name="channel_image"
                        checked={channelImageOption === option}
                        onChange={() => setChannelImageOption(option)}
                      />
                      <RadioCircle />
                    </StyledRadiosLabel>
                  ))}
                </ActionBox>
              </div>
              <div>
                {channelImageOption === 'custom' && (
                  <>
                    <LabelText $type="smallHeadline">
                      Add Channel Image
                    </LabelText>
                    {!!authorImages?.length ? (
                      <UploadedImages>{renderAuthorImages}</UploadedImages>
                    ) : (
                      <UploadAuthorImages
                        coverIndex={2}
                        setImages={setAuthorImages}
                      />
                    )}
                  </>
                )}
              </div>
              <div>
                <ActionTitle $type="smallHeadline">Channel Name</ActionTitle>
                <Input
                  placeholder="Add Name"
                  onChange={(e) => setAuthorName(e.target.value)}
                  value={authorName}
                />
              </div>
              <div>
                <LabelText $type="smallHeadline">Channel Name Color</LabelText>
                <ActionBox>
                  {CHANNEL_NAME_COLORS.map((color) => (
                    <StyledRadiosLabel key={color}>
                      {color}
                      <RadioInput
                        type="radio"
                        name="name_color"
                        checked={nameColor === color}
                        onChange={() => setNameColor(color)}
                      />
                      <RadioCircle />
                    </StyledRadiosLabel>
                  ))}
                </ActionBox>
              </div>
            </>
          )}
        </ModalContent>
        <SidePanelFooter $actions>
          <Button type="button" onClick={onUpdateSettings}>
            Apply
          </Button>
          <Button color="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
        </SidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default SettingsModal;
